"use client";
import { CoworldSwitcherView } from "@softmax/web-ux/shell";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { buildHref } from "@softmax/web-addresses";
import { useRoute } from "../../navigation/use-route";
import { FeatureLink as Link } from "../../navigation/feature-link";
import { useActualViewer } from "../../session/use-actual-viewer";
import { useDiscardUnavailableData } from "../../data/use-discard-unavailable-data";
import type { CoworldTarget } from "./coworld-switcher-data";
import {
  leagueDirectoryQueryOptions,
  leaguePortfolioQueryOptions,
} from "./league-directory";
import { siteShape } from "../site-shape";
import { switcherState } from "./switcher-state";

export function CoworldSwitcher({
  coworld,
}: {
  readonly coworld?: CoworldTarget | undefined;
}) {
  const route = useRoute();
  const viewer = useActualViewer();
  const viewerId = viewer.data?.subject_id ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const directoryOptions = leagueDirectoryQueryOptions({ viewerId });
  const portfolioOptions = leaguePortfolioQueryOptions();
  const directory = useQuery({
    ...directoryOptions,
    enabled: open && !viewer.isPending && !viewer.isError,
  });
  const portfolio = useQuery({
    ...portfolioOptions,
    enabled: open && viewerId !== null && !viewer.isError,
  });
  const directoryUnavailable = useDiscardUnavailableData({
    queryKey: directoryOptions.queryKey,
    error: directory.error,
  });
  const portfolioUnavailable = useDiscardUnavailableData({
    queryKey: portfolioOptions.queryKey,
    error: portfolio.error,
  });
  const entries = directoryUnavailable ? undefined : directory.data?.entries;
  const authorizedSeats = portfolioUnavailable ? undefined : portfolio.data;
  const seats = viewerId === null ? [] : authorizedSeats;
  const queries =
    viewerId === null ? [viewer, directory] : [viewer, directory, portfolio];
  const failed = queries.some((result) => result.isError);
  const pending = queries.some((result) => result.isFetching);
  const state = switcherState({
    entries,
    seats,
    current: route.area === "game" ? (coworld ?? null) : null,
    route,
    query,
    pending,
    failed,
  });

  return (
    <CoworldSwitcherView
      label={coworld?.name ?? "Coworlds"}
      compactTrigger={siteShape.signedInRail}
      open={open}
      onOpenChange={setOpen}
      query={query}
      onQueryChange={setQuery}
      state={state}
      onRetry={() => {
        void viewer.refetch();
        void directory.refetch();

        if (viewerId !== null) void portfolio.refetch();
      }}
      browseHref={buildHref({ name: "browse" })}
      renderLink={(props) => <Link {...props} />}
    />
  );
}
